import React, { useEffect, useState } from 'react'

import './searchableTab.scss'

import { Search } from 'components/search/Search'
import { colors } from 'styles/colors'
import { VeType } from 'storeTypes/vesTypes'
import { VillageType } from 'helpers'
import { useSearchParams } from 'react-router-dom'
import { useInfiniteScroll } from 'hooks'
import { EmptyList } from 'components/emptyList/EmptyList'

export type SearchItemType = VeType | Partial<VillageType>

type TabType = {
    text: string
    placeholderText: string
    noResultsText: string
    icon?: JSX.Element
    extraClass?: string
    renderItem: (item?: SearchItemType) => JSX.Element
    searchItems?: (items: SearchItemType[], query: string) => SearchItemType[]
    items: SearchItemType[]
    tabInnerComponent?: JSX.Element
}

interface TabProps {
    tabs: TabType[]
    tabBackgroundClassName?: string
    onActiveTabChange?: (tabName: number) => void
}

interface SearchAnalyticsProps {
    searchAnalyticsEventHandler?: (tabName: string) => void
}

type SearchableTabProps = TabProps & SearchAnalyticsProps

export const SearchableTab = ({
    tabs = [],
    searchAnalyticsEventHandler,
    tabBackgroundClassName = '',
    onActiveTabChange
}: SearchableTabProps) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const fromTab = parseInt(searchParams.get('from_tab') || '')

    const [query, setQuery] = useState('')
    const [activeTab, setActiveTab] = useState(tabs[fromTab || 0])

    const onTabChangeHandler = (text: string) => {
        const tab = tabs.find((t) => t.text === text)
        setActiveTab(tab || tabs[0])
        setQuery('')

        setSearchParams(`from_tab=${tabs.indexOf(tab || tabs[0])}`)

        onActiveTabChange?.(tabs.indexOf(tab || tabs[0]))
    }

    const onSearchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value)
    }

    const {
        renderItem,
        searchItems,
        items,
        placeholderText: placeholder,
        noResultsText,
        tabInnerComponent
    } = activeTab ?? {}
    const searchedItems = searchItems ? searchItems(items, query) : items

    const { renderedItems: renderedClients, resetItems } = useInfiniteScroll({
        filteredItems: searchedItems,
        createItemCard: renderItem,
        containerClassName: 'village-client--list-new-design'
    })

    useEffect(() => {
        resetItems()
    }, [items])

    if (!activeTab) {
        return null
    }

    return (
        <div className="tabs" aria-label="searchable-tab">
            <div className="tabs--buttons">
                {tabs.map((tab, index) => {
                    const classes = ['tabs--button']
                    const isActive = tab.text === activeTab.text

                    const tabQuery = isActive ? query : ''

                    if (isActive) {
                        classes.push('active')
                        classes.push(tab.extraClass || 'green')
                    }

                    return (
                        <span
                            key={index}
                            onClick={() => onTabChangeHandler(tab.text)}
                            className={classes.join(' ')}
                            aria-label={`tab ${tab.text}`}
                        >
                            {tab?.icon}
                            {tab.text} (
                            {searchItems
                                ? tab.searchItems?.(tab.items, tabQuery).length
                                : tab.items.length}
                            )
                        </span>
                    )
                })}
            </div>
            <div
                className={['tabs--content', tabBackgroundClassName].join(' ')}
            >
                {searchItems && (
                    <div className="tabs--search">
                        <Search
                            iconSize="30"
                            iconPos="left"
                            iconColor={colors.grey}
                            style="compact"
                            onChange={onSearchHandler}
                            value={query}
                            placeholder={placeholder || ''}
                            onBlur={() =>
                                searchAnalyticsEventHandler?.(activeTab.text)
                            }
                        />
                    </div>
                )}
                {tabInnerComponent}
                {
                    <div className="tabs--items">
                        {searchedItems.length === 0 && (
                            <div className="tabs--empty">
                                <EmptyList message={noResultsText} />
                            </div>
                        )}
                        {renderedClients}
                    </div>
                }
            </div>
        </div>
    )
}
